import { useEffect } from 'react';
import { X, Zap, MapPin, Calendar, Fuel, Cpu, Wrench, Droplets, Battery, StickyNote, Navigation } from 'lucide-react';
import { formatDate, formatCoolingType } from '@shared/utils';
import { DetailSection, DetailRow, StatPill, getDirectionsUrl } from './DetailSection';

const monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export default function FranklinSiteDetailsModal({ site, isOpen, onClose }) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const s = site || {};

  const formatCoordinate = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = typeof value === 'string' ? parseFloat(value) : value;
    if (Number.isNaN(parsed)) return value;
    return parsed.toFixed(6);
  };

  const getServiceMonths = (months) => {
    if (!months) return null;
    const normalized = Array.isArray(months) ? months : [];
    return normalized.map((m) => monthLabels[m - 1]).filter(Boolean).join(', ');
  };

  if (!isOpen) return null;

  const lat = formatCoordinate(s.latitude);
  const lon = formatCoordinate(s.longitude);
  const hasCoords = lat && lon;
  const mapUrl = hasCoords ? `https://maps.google.com/?q=${s.latitude},${s.longitude}` : null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-50">
      <div className="fixed inset-0 flex h-full w-full flex-col">
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider text-amber-100">Franklin Site Details</p>
              <h3 className="text-xl font-bold text-white mt-1">{s.locationName || s.siteId || 'Loading...'}</h3>
            </div>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-amber-100 hover:bg-white/10 hover:text-white transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          {site && (
            <div className="flex flex-wrap gap-2 mt-4">
              <StatPill
                label="Status"
                value={s.isActive ? 'Active' : 'Inactive'}
                variant={s.isActive ? 'success' : 'danger'}
              />
              {s.generatorType && (
                <StatPill label="Generator" value={s.generatorType} />
              )}
              {s.lastServiceDate && (
                <StatPill label="Last Service" value={formatDate(s.lastServiceDate)} />
              )}
              {s.serviceCycleMonths?.length > 0 && (
                <StatPill label="Service Months" value={getServiceMonths(s.serviceCycleMonths)} />
              )}
            </div>
          )}
        </div>

        {/* Scrollable Body */}
        <div className="flex-1 overflow-y-auto overscroll-contain touch-pan-y px-4 py-5">
          {!site ? (
            <div className="flex h-full items-center justify-center text-sm text-gray-500">
              Loading site details...
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              <DetailSection icon={Zap} title="Site Information" color="amber">
                <DetailRow label="Site ID" value={s.siteId} />
                <DetailRow label="Company" value={s.company} />
                <DetailRow label="Location Name" value={s.locationName} />
                <DetailRow label="Active" value={s.isActive ? 'Yes' : 'No'} />
              </DetailSection>

              <DetailSection icon={Calendar} title="Service Information" color="purple">
                <DetailRow label="Service Months" value={getServiceMonths(s.serviceCycleMonths)} />
                <DetailRow label="Oil Change Month" value={s.oilChangeMonth ? monthLabels[s.oilChangeMonth - 1] : null} />
                <DetailRow label="Last Service Date" value={s.lastServiceDate ? formatDate(s.lastServiceDate) : null} />
                <DetailRow label="Month/Year Placed" value={s.monthYearPlaced} />
              </DetailSection>

              <DetailSection icon={MapPin} title="Address" color="amber" span>
                <DetailRow label="Address" value={s.address} />
                <DetailRow label="City" value={s.city} />
                <DetailRow label="District" value={s.district} />
                <DetailRow label="County" value={s.county} />
                <DetailRow label="State" value={s.state} />
                <DetailRow label="ZIP Code" value={s.zipCode} />
                <DetailRow label="Coordinates" value={hasCoords ? (
                  <a href={mapUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                    {lat}, {lon}
                  </a>
                ) : null} />
              </DetailSection>

              <DetailSection icon={Fuel} title="Equipment" color="orange">
                <DetailRow label="Backup Power" value={s.backupPower} />
                <DetailRow label="Tank Size" value={s.tankSize} />
                <DetailRow label="Herring Gas Ref" value={s.herringGasRef} />
              </DetailSection>

              <DetailSection icon={Cpu} title="Generator Information" color="green">
                <DetailRow label="Type" value={s.generatorType} />
                <DetailRow label="Model" value={s.generatorModel} />
                <DetailRow label="Serial Number" value={s.generatorSerialNumber} />
                <DetailRow label="Size / Capacity" value={s.generatorSize} />
                <DetailRow label="Cooling" value={formatCoolingType(s.generatorCoolingType)} />
                <DetailRow label="Exercise Time" value={s.generatorExerciseTime} />
              </DetailSection>

              <DetailSection icon={Wrench} title="Maintenance Parts" color="orange">
                <DetailRow label="Air Filter" value={s.airFilter} />
                <DetailRow label="Fuel Filter 1" value={s.fuelFilter1} />
                <DetailRow label="Fuel Filter 2" value={s.fuelFilter2} />
                <DetailRow label="Oil Filter 1" value={s.oilFilter1} />
                <DetailRow label="Oil Filter 2" value={s.oilFilter2} />
              </DetailSection>

              <DetailSection icon={Droplets} title="Fluids" color="cyan">
                <DetailRow label="Fuel Type" value={s.fuelType} />
                <DetailRow label="Fuel Additive" value={s.fuelAdditive} />
                <DetailRow label="Oil Type" value={s.oilType} />
                <DetailRow label="Oil Amount" value={s.oilAmount} />
              </DetailSection>

              <DetailSection icon={Battery} title="Battery" color="yellow">
                <DetailRow label="Battery Size" value={s.batterySize} />
                <DetailRow label="Battery Date Code" value={s.batteryDateCode} />
                <DetailRow label="Battery Date Changed" value={s.batteryDateChanged ? formatDate(s.batteryDateChanged) : null} />
              </DetailSection>

              {s.notes && (
                <DetailSection icon={StickyNote} title="Notes" color="slate" span>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{s.notes}</p>
                </DetailSection>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-slate-200 bg-white px-6 py-4">
          <div>
            {site && (() => {
              const dirUrl = getDirectionsUrl({ latitude: s.latitude, longitude: s.longitude, address: s.address, city: s.city, state: s.state, zipCode: s.zipCode });
              return dirUrl ? (
                <a
                  href={dirUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="btn bg-blue-500 text-white hover:bg-blue-600 focus:ring-blue-400"
                >
                  <Navigation className="h-4 w-4 mr-1.5" />
                  Directions
                </a>
              ) : null;
            })()}
          </div>
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
